import {
  CheckCircle,
  Lock,
  MessageCircle,
  Scale,
  Star,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import { useI18n } from '../../i18n';

export default function WhyChooseUsSection() {
  const { t } = useI18n();

  const features = [
    {
      icon: Scale,
      ...t.whyChooseUs.features.transparentComparison,
    },
    {
      icon: Zap,
      ...t.whyChooseUs.features.fastOffers,
    },
    {
      icon: Lock,
      ...t.whyChooseUs.features.secureContracts,
    },
    {
      icon: CheckCircle,
      ...t.whyChooseUs.features.licensedProviders,
    },
    {
      icon: MessageCircle,
      ...t.whyChooseUs.features.continuousSupport,
    },
  ];

  return (
    <motion.section
      id="why-choose-us"
      className="relative overflow-hidden bg-white px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="relative z-10">
        <motion.div
          className="mb-10 text-center"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <motion.div
            className="flex flex-col items-center justify-center gap-6"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
              {t.whyChooseUs.title}
            </h2>
            <motion.div
              className="mx-auto h-1 w-full max-w-4xl bg-emerald-500"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              viewport={{ once: true }}
            />
          </motion.div>
        </motion.div>

        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isLastItem = index === features.length - 1;

            return (
              <motion.div
                key={index}
                className={`relative overflow-hidden rounded-xl bg-white p-6 shadow-lg ${
                  isLastItem ? 'md:col-span-2 md:mx-auto md:w-1/2' : ''
                }`}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{
                  delay: 0.2 + index * 0.1,
                  duration: 0.6,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  transition: { duration: 0.2 },
                }}
              >
                <div className="absolute inset-y-0 start-0 w-1 bg-emerald-500" />

                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex items-center gap-3">
                    <Icon className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
                    <h3 className="text-lg font-bold text-emerald-600 sm:text-xl">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}
